import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  listDids,
  listDidGroups,
  createDidGroup,
  createDid,
  bulkCreateDids,
  provisionDids,
  bulkMoveDids,
  bulkDidAction,
  updateDid,
  releaseDid,
  clearDidBlocks,
  getDidReputation,
  listQuarantinePending,
  purgeQuarantinedDid,
  type DidListParams,
  type Did,
  type DidGroup,
} from "../api/dids";
import { listEligibleCampaigns } from "../api/queues";
import { ListOveragePlannerPanel } from "../components/ListOveragePlannerPanel";

const PAGE_SIZE = 50;

function formatForwarding(d: Did) {
  if (!d.action_type || !d.action_data) return "Default / Unset";
  const modeTag = d.inbound_mode ? ` · ${d.inbound_mode}` : "";
  if (d.action_type === "external") return `Forward → ${d.action_data}${modeTag}`;
  if (d.action_type === "agents") return `Agents (#${d.action_data})${modeTag}`;
  if (d.action_type === "sip") return `SIP → ${d.action_data}${modeTag}`;
  return `${d.action_type}: ${d.action_data}${modeTag}`;
}

function NewDidModal({
  groups,
  onClose,
  onSaved,
}: {
  groups: DidGroup[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"single" | "bulk" | "carrier" | "list_overage">("single");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [bulkPhones, setBulkPhones] = useState("");
  const [namePrefix, setNamePrefix] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [quantity, setQuantity] = useState(5);
  const [groupId, setGroupId] = useState<string>("");
  const [newPoolName, setNewPoolName] = useState("");
  const [showNewPoolInput, setShowNewPoolInput] = useState(false);

  const createPoolMut = useMutation({
    mutationFn: () => createDidGroup(newPoolName.trim()),
    onSuccess: (createdId) => {
      qc.invalidateQueries({ queryKey: ["did-groups"] });
      setGroupId(String(createdId));
      setNewPoolName("");
      setShowNewPoolInput(false);
    },
  });

  const saveSingle = useMutation({
    mutationFn: () =>
      createDid({
        name: name.trim(),
        phone: phone.trim(),
        did_group_id: groupId ? Number(groupId) : null,
      }),
    onSuccess: onSaved,
  });

  const saveBulk = useMutation({
    mutationFn: () => {
      const phones = bulkPhones
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      return bulkCreateDids({
        phones,
        name_prefix: namePrefix.trim() || undefined,
        did_group_id: groupId ? Number(groupId) : null,
      });
    },
    onSuccess: onSaved,
  });

  const saveCarrier = useMutation({
    mutationFn: () =>
      provisionDids({
        area_code: areaCode.trim(),
        quantity: Number(quantity) || 1,
        did_group_id: groupId ? Number(groupId) : null,
      }),
    onSuccess: onSaved,
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-20 p-4 overflow-y-auto"
      data-testid="new-did-modal"
    >
      <div
        className={`bg-white rounded-lg shadow-xl p-6 w-full ${
          tab === "list_overage" ? "max-w-2xl" : "max-w-md"
        } space-y-4 my-auto`}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Add / Order Phone Numbers</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">
            ✕
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex flex-wrap gap-1.5 border-b border-gray-200 pb-2">
          <button
            type="button"
            onClick={() => setTab("single")}
            data-testid="new-did-tab-single"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "single"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Single Number
          </button>
          <button
            type="button"
            onClick={() => setTab("bulk")}
            data-testid="new-did-tab-bulk"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "bulk"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Bulk Paste
          </button>
          <button
            type="button"
            onClick={() => setTab("carrier")}
            data-testid="new-did-tab-carrier"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "carrier"
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Order by Area Code
          </button>
          <button
            type="button"
            onClick={() => setTab("list_overage")}
            data-testid="new-did-tab-list-overage"
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              tab === "list_overage"
                ? "bg-indigo-600 text-white"
                : "bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
            }`}
          >
            Buy Overage by List
          </button>
        </div>

        {/* Target Pool Selector + Quick Create Pool */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="block text-xs text-gray-600 font-medium">
              Assign to Caller ID Pool (optional)
            </label>
            <button
              type="button"
              onClick={() => setShowNewPoolInput((v) => !v)}
              className="text-xs text-brand hover:underline"
            >
              {showNewPoolInput ? "Cancel new pool" : "+ Create new pool"}
            </button>
          </div>
          {showNewPoolInput ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newPoolName}
                onChange={(e) => setNewPoolName(e.target.value)}
                placeholder="New pool name…"
                className="flex-1 border border-gray-300 rounded px-2.5 py-1.5 text-sm"
              />
              <button
                type="button"
                disabled={!newPoolName.trim() || createPoolMut.isPending}
                onClick={() => createPoolMut.mutate()}
                className="bg-brand text-white rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
              >
                Create
              </button>
            </div>
          ) : (
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              data-testid="new-did-pool-select"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">-- Leave Unassigned --</option>
              {groups.map((g) => (
                <option key={g.did_group_id} value={String(g.did_group_id)}>
                  {g.name} ({g.member_count})
                </option>
              ))}
            </select>
          )}
        </div>

        {tab === "single" && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Phone Number (10-digit US)</label>
              <input
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                placeholder="4125550123"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="single-did-phone-input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Label (optional)</label>
              <input
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Main Line"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="single-did-name-input"
              />
            </div>
            {saveSingle.isError && (
              <p className="text-xs text-red-600">{(saveSingle.error as Error).message}</p>
            )}
            <div className="flex gap-2 pt-2">
              <button
                disabled={!phone.trim() || saveSingle.isPending}
                onClick={() => saveSingle.mutate()}
                data-testid="save-single-did-btn"
                className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm font-medium disabled:opacity-50"
              >
                {saveSingle.isPending ? "Saving…" : "Add Number"}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {tab === "bulk" && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Phone Numbers (one per line or comma-separated)
              </label>
              <textarea
                rows={4}
                value={bulkPhones}
                onChange={(e) => setBulkPhones(e.target.value)}
                placeholder={"4125550111\n4125550112"}
                data-testid="bulk-did-phones-input"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Label Prefix (optional)</label>
              <input
                type="text"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder="Batch A"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            {saveBulk.isError && (
              <p className="text-xs text-red-600">{(saveBulk.error as Error).message}</p>
            )}
            <div className="flex gap-2 pt-2">
              <button
                disabled={!bulkPhones.trim() || saveBulk.isPending}
                onClick={() => saveBulk.mutate()}
                data-testid="save-bulk-did-btn"
                className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm font-medium disabled:opacity-50"
              >
                {saveBulk.isPending ? "Importing…" : "Import Numbers"}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {tab === "carrier" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Area Code (3 digits)</label>
                <input
                  type="text"
                  maxLength={3}
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="412"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            {saveCarrier.isError && (
              <p className="text-xs text-red-600">{(saveCarrier.error as Error).message}</p>
            )}
            <div className="flex gap-2 pt-2">
              <button
                disabled={areaCode.length !== 3 || quantity < 1 || saveCarrier.isPending}
                onClick={() => saveCarrier.mutate()}
                className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm font-medium disabled:opacity-50"
              >
                {saveCarrier.isPending
                  ? "Ordering from Carrier…"
                  : `Order ${quantity} Number(s)`}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {tab === "list_overage" && (
          <ListOveragePlannerPanel
            groupId={groupId ? Number(groupId) : null}
            groups={groups}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function ReputationModal({ did, onClose }: { did: Did; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["did-reputation", did.did_id],
    queryFn: () => getDidReputation(did.did_id),
  });
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-full overflow-auto space-y-4">
        <h2 className="text-lg font-bold">
          Reputation — <span className="font-mono">{did.phone}</span>
        </h2>
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : !data ? (
          <p className="text-gray-500 text-sm">No data.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs uppercase text-gray-400">Hourly cap</div>
                <div className="text-lg font-bold">{data.hourly_cap}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs uppercase text-gray-400">Daily cap</div>
                <div className="text-lg font-bold">{data.daily_cap}</div>
              </div>
            </div>
            {data.warmup_active && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">
                Warmup ramp active — caps are reduced for the first 7 days
                {data.age_days != null ? ` (age ${data.age_days}d)` : ""}.
              </p>
            )}

            <div>
              <h3 className="text-sm font-semibold mb-1">7-day call quality</h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-1 text-gray-500">Attempts</td>
                    <td className="py-1 text-right">{data.attempts_7d.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-500">Connected</td>
                    <td className="py-1 text-right">{data.connected_7d.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-500">Connect rate</td>
                    <td className="py-1 text-right">{pct(data.connect_rate)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-500">SIP 603 (carrier decline)</td>
                    <td
                      className={`py-1 text-right ${
                        data.sip603_rate >= 0.25 ? "text-red-600 font-semibold" : ""
                      }`}
                    >
                      {pct(data.sip603_rate)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-500">Fast hangup (≤2s)</td>
                    <td
                      className={`py-1 text-right ${
                        data.fast_hangup_rate >= 0.6 ? "text-red-600 font-semibold" : ""
                      }`}
                    >
                      {pct(data.fast_hangup_rate)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 text-gray-500">Recipients in cooldown</td>
                    <td className="py-1 text-right">
                      {data.recipient_cooldown_count.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-1">Active carrier blocks</h3>
              {data.carrier_blocks.length === 0 ? (
                <p className="text-sm text-green-700">None — number is clear.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-500">
                    <tr>
                      <th className="py-1">Carrier</th>
                      <th className="py-1">Reason</th>
                      <th className="py-1 text-right">Hits</th>
                      <th className="py-1 text-right">Until</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.carrier_blocks.map((b, i) => (
                      <tr key={i}>
                        <td className="py-1">{b.dest_carrier}</td>
                        <td className="py-1">{b.reason}</td>
                        <td className="py-1 text-right">{b.block_count}</td>
                        <td className="py-1 text-right text-gray-500">
                          {b.blocked_until ? new Date(b.blocked_until).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        <div className="pt-2">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function RoutingModal({
  did,
  onClose,
  onSaved,
}: {
  did: Did;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<"agents" | "external" | "sip">(
    did.action_type === "agents"
      ? "agents"
      : did.action_type === "sip"
      ? "sip"
      : "external",
  );
  const [campaignId, setCampaignId] = useState<string>(
    did.action_type === "agents" ? did.action_data ?? "" : "",
  );
  const [number, setNumber] = useState<string>(
    did.action_type === "external" ? did.action_data ?? "" : "",
  );
  const [sipUri, setSipUri] = useState<string>(
    did.action_type === "sip" ? did.action_data ?? "" : "",
  );
  const [inboundMode, setInboundMode] = useState<"direct" | "press1" | "press3">(
    (did.inbound_mode as "direct" | "press1" | "press3") || "press1",
  );
  const [error, setError] = useState<string | null>(null);

  const { data: campaigns = [] } = useQuery({
    queryKey: ["eligible-campaigns"],
    queryFn: listEligibleCampaigns,
  });

  const save = useMutation({
    mutationFn: () => {
      if (mode === "agents") {
        return updateDid(did.did_id, {
          action_type: "agents",
          action_data: campaignId,
          inbound_mode: inboundMode,
        });
      }
      if (mode === "sip") {
        return updateDid(did.did_id, {
          action_type: "sip",
          action_data: sipUri,
          inbound_mode: inboundMode,
        });
      }
      return updateDid(did.did_id, {
        action_type: "external",
        action_data: number,
        inbound_mode: inboundMode,
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e: unknown) => setError((e as Error).message || "Save failed"),
  });

  const canSave =
    mode === "agents"
      ? campaignId !== ""
      : mode === "sip"
      ? sipUri.trim().includes("@")
      : number.trim() !== "";

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">
          Inbound routing — <span className="font-mono">{did.phone}</span>
        </h2>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="routing-mode"
            checked={mode === "agents"}
            onChange={() => setMode("agents")}
            aria-label="Ring my agents"
          />
          Ring my agents
        </label>
        {mode === "agents" && (
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">Select a campaign…</option>
            {campaigns.map((c) => (
              <option key={c.campaign_id} value={String(c.campaign_id)}>
                {c.name} ({c.agent_count} agent{c.agent_count === 1 ? "" : "s"})
              </option>
            ))}
          </select>
        )}
        {mode === "agents" && campaigns.length === 0 && (
          <p className="text-xs text-gray-500">
            No campaigns have agents assigned yet. Assign agents to a campaign first.
          </p>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="routing-mode"
            checked={mode === "external"}
            onChange={() => setMode("external")}
            aria-label="Forward to a number"
          />
          Forward to a phone number
        </label>
        {mode === "external" && (
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder="(412) 536-4219"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="routing-mode"
            checked={mode === "sip"}
            onChange={() => setMode("sip")}
            aria-label="Forward to SIP URI"
          />
          Forward to SIP URI
        </label>
        {mode === "sip" && (
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder="1001@sip.example.com"
            value={sipUri}
            onChange={(e) => setSipUri(e.target.value)}
          />
        )}

        <div className="pt-1">
          <label className="block text-xs text-gray-500 mb-1">Inbound Callback Mode</label>
          <select
            value={inboundMode}
            onChange={(e) => setInboundMode(e.target.value as "direct" | "press1" | "press3")}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="direct">Direct Connect (Immediate Ring)</option>
            <option value="press1">Press-1 IVR Gate (Press 1 to Connect / DNC on 9)</option>
            <option value="press3">Press-3 IVR Gate (Press 3 to Connect / DNC on 7)</option>
          </select>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 pt-2">
          <button
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function DidsPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [repDid, setRepDid] = useState<Did | null>(null);
  const [routeDid, setRouteDid] = useState<Did | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [poolFilter, setPoolFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkTargetPool, setBulkTargetPool] = useState<string>("");

  const [params, setParams] = useState<DidListParams>({
    page: 1,
    per_page: PAGE_SIZE,
    include_retired: false,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["did-groups"],
    queryFn: listDidGroups,
  });

  const effectiveParams: DidListParams = {
    ...params,
    q: searchInput.trim() || undefined,
    unassigned: poolFilter === "unassigned" ? true : undefined,
    did_group_id:
      poolFilter !== "all" && poolFilter !== "unassigned" ? Number(poolFilter) : undefined,
  };

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["dids", effectiveParams],
    queryFn: () => listDids(effectiveParams),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["dids"] });
    qc.invalidateQueries({ queryKey: ["did-groups"] });
    qc.invalidateQueries({ queryKey: ["did-group"] });
  };

  const quarantine = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) => updateDid(id, { is_quarantined: on }),
    onSuccess: invalidate,
  });
  const release = useMutation({
    mutationFn: (id: number) => releaseDid(id),
    onSuccess: invalidate,
  });
  const clearBlocks = useMutation({
    mutationFn: (id: number) => clearDidBlocks(id),
    onSuccess: invalidate,
  });
  const assignPoolMut = useMutation({
    mutationFn: ({ didId, groupId }: { didId: number; groupId: number | null }) =>
      bulkMoveDids({ did_ids: [didId], target_group_id: groupId }),
    onSuccess: invalidate,
  });
  const renameMut = useMutation({
    mutationFn: ({ didId, name }: { didId: number; name: string }) => updateDid(didId, { name }),
    onSuccess: invalidate,
  });
  const bulkMoveMut = useMutation({
    mutationFn: (targetGroupId: number | null) =>
      bulkMoveDids({ did_ids: [...selectedIds], target_group_id: targetGroupId }),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidate();
    },
  });
  const bulkActMut = useMutation({
    mutationFn: (action: "quarantine" | "unquarantine" | "clear_blocks" | "retire") =>
      bulkDidAction({ did_ids: [...selectedIds], action }),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const [purge, setPurge] = useState<{
    total: number;
    done: number;
    failed: number;
    current: string;
    running: boolean;
  } | null>(null);
  const stopRef = useRef(false);

  async function runPurge() {
    let pending;
    try {
      pending = await listQuarantinePending();
    } catch (e) {
      alert(`Could not load quarantined numbers: ${(e as Error).message}`);
      return;
    }
    if (pending.count === 0) {
      alert("No quarantined numbers to purge.");
      return;
    }
    if (
      !confirm(
        `Release ${pending.count.toLocaleString()} quarantined number(s) from the carrier and ` +
          `delete them locally? This is irreversible.`,
      )
    )
      return;
    stopRef.current = false;
    setPurge({ total: pending.count, done: 0, failed: 0, current: "", running: true });
    let done = 0;
    let failed = 0;
    for (const d of pending.dids) {
      if (stopRef.current) break;
      setPurge((s) => (s ? { ...s, current: d.phone } : s));
      try {
        const r = await purgeQuarantinedDid(d.did_id);
        if (r.status === "deleted") done += 1;
        else failed += 1;
      } catch {
        failed += 1;
      }
      setPurge((s) => (s ? { ...s, done, failed } : s));
    }
    setPurge((s) => (s ? { ...s, running: false, current: "" } : s));
    invalidate();
  }

  const rows = data?.data ?? [];
  const allPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.did_id));
  const toggleAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        rows.forEach((r) => next.delete(r.did_id));
      } else {
        rows.forEach((r) => next.add(r.did_id));
      }
      return next;
    });
  };
  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const page = params.page ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Phone Numbers (DIDs)</h1>
          <p className="text-sm text-gray-500">
            Search, filter, assign numbers to Caller ID pools, and configure inbound routing.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/callerid-groups"
            data-testid="goto-pools-link"
            className="px-3.5 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            Manage Pools ({groups.length})
          </Link>
          <button
            onClick={runPurge}
            disabled={purge?.running}
            className="bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2 text-sm disabled:opacity-50"
            title="Release every quarantined number from the carrier, then delete it locally"
          >
            {purge?.running ? "Purging…" : "Purge quarantined"}
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="open-new-did-modal-btn"
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm font-medium"
          >
            + Add / Order Numbers
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white rounded-lg shadow p-3 mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setParams((p) => ({ ...p, page: 1 }));
          }}
          placeholder="Search by phone number, area code, or label…"
          data-testid="dids-search-input"
          className="flex-1 min-w-[220px] border border-gray-300 rounded px-3 py-1.5 text-sm"
        />
        <select
          value={poolFilter}
          onChange={(e) => {
            setPoolFilter(e.target.value);
            setParams((p) => ({ ...p, page: 1 }));
          }}
          data-testid="dids-pool-filter-select"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
        >
          <option value="all">All Pools & Unassigned</option>
          <option value="unassigned">Unassigned Only</option>
          {groups.map((g) => (
            <option key={g.did_group_id} value={String(g.did_group_id)}>
              Pool: {g.name} ({g.member_count})
            </option>
          ))}
        </select>
        <select
          value={params.status_filter ?? ""}
          onChange={(e) => {
            const val = e.target.value as DidListParams["status_filter"] | "";
            setParams((p) => ({
              ...p,
              status_filter: val ? val : undefined,
              page: 1,
            }));
          }}
          data-testid="dids-status-filter-select"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
        >
          <option value="">All Active Statuses</option>
          <option value="active">Clean / Active Only</option>
          <option value="quarantined">Quarantined</option>
          <option value="blocked">Carrier Blocked</option>
          <option value="retired">Retired</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-gray-600 ml-auto">
          <input
            type="checkbox"
            checked={params.include_retired ?? false}
            onChange={(e) =>
              setParams((p) => ({ ...p, include_retired: e.target.checked, page: 1 }))
            }
          />
          Show retired
        </label>
      </div>

      {/* Multi-Select Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div
          className="mb-4 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg flex flex-wrap items-center justify-between gap-2 text-xs"
          data-testid="dids-bulk-action-bar"
        >
          <span className="font-semibold text-blue-900">
            {selectedIds.size} number{selectedIds.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={bulkTargetPool}
              onChange={(e) => setBulkTargetPool(e.target.value)}
              data-testid="dids-bulk-move-select"
              className="border border-blue-300 rounded px-2.5 py-1 text-xs bg-white"
            >
              <option value="">Move selected to pool…</option>
              <option value="unassign">-- Unassign from Pool --</option>
              {groups.map((g) => (
                <option key={g.did_group_id} value={String(g.did_group_id)}>
                  Pool: {g.name} ({g.member_count})
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!bulkTargetPool || bulkMoveMut.isPending}
              onClick={() =>
                bulkMoveMut.mutate(bulkTargetPool === "unassign" ? null : Number(bulkTargetPool))
              }
              data-testid="dids-bulk-move-btn"
              className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1 font-medium disabled:opacity-50"
            >
              {bulkMoveMut.isPending ? "Moving…" : "Apply Pool Move"}
            </button>
            <button
              type="button"
              disabled={bulkActMut.isPending}
              onClick={() => bulkActMut.mutate("clear_blocks")}
              className="bg-white border border-green-300 hover:bg-green-50 text-green-800 rounded px-2.5 py-1"
            >
              Clear Blocks
            </button>
            <button
              type="button"
              disabled={bulkActMut.isPending}
              onClick={() => bulkActMut.mutate("quarantine")}
              className="bg-white border border-amber-300 hover:bg-amber-50 text-amber-800 rounded px-2.5 py-1"
            >
              Quarantine
            </button>
            <button
              type="button"
              disabled={bulkActMut.isPending}
              onClick={() => bulkActMut.mutate("unquarantine")}
              className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 rounded px-2.5 py-1"
            >
              Unquarantine
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-gray-500 hover:underline ml-1"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {purge && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
          <div className="flex items-center justify-between">
            <span>
              Purging quarantined numbers: {(purge.done + purge.failed).toLocaleString()} /{" "}
              {purge.total.toLocaleString()} released
              {purge.failed > 0 && (
                <span className="text-red-600"> · {purge.failed.toLocaleString()} failed</span>
              )}
              {purge.running && purge.current && (
                <span className="text-gray-500 font-mono"> · {purge.current}</span>
              )}
            </span>
            {purge.running ? (
              <button
                onClick={() => {
                  stopRef.current = true;
                }}
                className="text-red-700 hover:underline"
              >
                Stop
              </button>
            ) : (
              <span className="text-green-700">
                Done
                {purge.failed > 0 ? ` — ${purge.failed.toLocaleString()} failed (see logs)` : ""}
              </span>
            )}
          </div>
          <div className="mt-2 h-2 bg-amber-100 rounded overflow-hidden">
            <div
              className="h-full bg-red-500 transition-all"
              style={{ width: pct((purge.done + purge.failed) / Math.max(purge.total, 1)) }}
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} numbers`}
          </div>
          <table className="w-full text-sm" data-testid="dids-table">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleAllPage}
                    data-testid="dids-select-all-page"
                  />
                </th>
                <th className="px-3 py-2">Number</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Caller ID Pool</th>
                <th className="px-3 py-2">Inbound Routing</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-4 text-center text-gray-500">
                    No DIDs match the current filter.
                  </td>
                </tr>
              ) : (
                rows.map((d: Did) => (
                  <tr key={d.did_id} className="hover:bg-gray-50/70" data-testid={`did-row-${d.phone}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(d.did_id)}
                        onChange={() => toggleOne(d.did_id)}
                        data-testid={`did-checkbox-${d.phone}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono font-medium">{d.phone}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => {
                          const next = window.prompt("Edit number label:", d.name || "");
                          if (next !== null) renameMut.mutate({ didId: d.did_id, name: next.trim() });
                        }}
                        title="Click to edit label"
                        className="text-left hover:underline text-gray-700"
                      >
                        {d.name || "—"}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={d.did_group_id ? String(d.did_group_id) : ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          assignPoolMut.mutate({
                            didId: d.did_id,
                            groupId: val ? Number(val) : null,
                          });
                        }}
                        data-testid={`did-pool-select-${d.phone}`}
                        className="border border-gray-200 rounded px-2 py-1 text-xs bg-white"
                      >
                        <option value="">-- Unassigned --</option>
                        {groups.map((g) => (
                          <option key={g.did_group_id} value={String(g.did_group_id)}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{formatForwarding(d)}</td>
                    <td className="px-3 py-2">
                      {d.is_retired ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700">
                          retired
                        </span>
                      ) : d.is_blocked ? (
                        <span
                          className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800"
                          title={
                            d.blocked_until
                              ? `until ${new Date(d.blocked_until).toLocaleString()}`
                              : ""
                          }
                        >
                          blocked{d.block_reason ? `: ${d.block_reason}` : ""}
                        </span>
                      ) : d.is_quarantined ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                          quarantined
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2.5 whitespace-nowrap text-xs">
                      <button onClick={() => setRepDid(d)} className="text-brand hover:underline">
                        Reputation
                      </button>
                      {!d.is_retired && (
                        <>
                          {(d.is_blocked || d.is_quarantined) && (
                            <button
                              onClick={() => clearBlocks.mutate(d.did_id)}
                              className="text-brand hover:underline"
                            >
                              Clear blocks
                            </button>
                          )}
                          <button
                            onClick={() =>
                              quarantine.mutate({ id: d.did_id, on: !d.is_quarantined })
                            }
                            className="text-brand hover:underline"
                          >
                            {d.is_quarantined ? "Unquarantine" : "Quarantine"}
                          </button>
                          <button
                            onClick={() => setRouteDid(d)}
                            className="text-brand hover:underline"
                          >
                            Routing
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Release (retire) ${d.phone}?`))
                                release.mutate(d.did_id);
                            }}
                            className="text-red-600 hover:underline"
                          >
                            Release
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showNew && (
        <NewDidModal
          groups={groups}
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            invalidate();
          }}
        />
      )}
      {repDid && <ReputationModal did={repDid} onClose={() => setRepDid(null)} />}
      {routeDid && (
        <RoutingModal
          did={routeDid}
          onClose={() => setRouteDid(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}
